import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Button, Typography, Alert, CircularProgress,
  Stepper, Step, StepLabel, Divider, Chip,
} from '@mui/material';
import { Payment, HourglassTop, CheckCircle, Apartment, PersonSearch, AddBusiness, Home, School } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const COMMITMENT_FEE = 5000;

const STATUS_STEP = { PendingPayment: 0, Provisioning: 1, Active: 2 };

// Inquiry lifecycle → where it sits on the progress tracker + how we describe it.
// (draft/rejected aren't "in progress", so they keep the register/edit layout below.)
const INQUIRY_STATUS = {
  pending:  {
    step: 1, chip: 'Under review', color: 'warning',
    title: 'Your inquiry is under review',
    note: "Our team is reviewing your inquiry. We'll email you and update this page as it progresses — you can still edit it until it's reviewed.",
  },
  approved: {
    step: 2, chip: 'Approved — being set up', color: 'info',
    title: 'Your school is being set up',
    note: "Great news — your inquiry was approved! We're preparing your school now. You'll be asked to sign in again once it's ready.",
  },
};

const SchoolPending = () => {
  const navigate = useNavigate();
  const [school,  setSchool]  = useState(JSON.parse(localStorage.getItem('school') || 'null'));
  const [error,   setError]   = useState('');
  const [paying,  setPaying]  = useState(false);
  const [loading, setLoading] = useState(true);
  const [noSchool, setNoSchool] = useState(false);   // waiting for an admin to assign one
  const [inquiry, setInquiry] = useState(null);      // this user's school inquiry, if any
  const pollRef = useRef(null);

  const cacheSchool = (s) => {
    if (!s) return;
    localStorage.setItem('school', JSON.stringify({
      id: s._id || s.id, code: s.code, name: s.name,
      provisionStatus: s.provisionStatus, county: s.county,
    }));
  };

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/schools/current`, { headers: hdrs() });
      if (r.data) {
        const localUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (noSchool || !localUser.hasSchool) {
          // Linked/assigned to a school AFTER this session's login — the token
          // predates it and has no dedicated-DB claim, so a fresh sign-in is
          // needed to route to the school's database.
          ['token', 'user', 'school'].forEach(k => localStorage.removeItem(k));
          navigate('/login', { state: { info: `You've been assigned to ${r.data.name}. Please sign in again to enter your school.` } });
          return;
        }
        setSchool({ ...r.data, id: r.data._id });
        cacheSchool(r.data);
        if (r.data.provisionStatus === 'Active') {
          navigate('/dashboard');
        }
      } else {
        // No school yet — wait here until an admin assigns one
        setNoSchool(true);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load your school status');
    } finally { setLoading(false); }
  }, [navigate, noSchool]);

  useEffect(() => { refresh(); }, [refresh]);

  // Load this user's inquiry (if any) so we can show its status while they wait
  useEffect(() => {
    axios.get(`${API}/api/school-inquiries/mine`, { headers: hdrs() })
      .then(r => setInquiry(r.data))
      .catch(() => { /* none */ });
  }, []);

  // Poll while provisioning, or while waiting for a school assignment
  useEffect(() => {
    if (school?.provisionStatus === 'Provisioning' || noSchool) {
      pollRef.current = setInterval(refresh, 15000);
      return () => clearInterval(pollRef.current);
    }
  }, [school?.provisionStatus, noSchool, refresh]);

  const handlePay = async () => {
    if (!school?.id && !school?._id) return;
    setPaying(true); setError('');
    try {
      const id = school.id || school._id;
      const r = await axios.post(`${API}/api/schools/${id}/pay-commitment`, {}, { headers: hdrs() });
      setSchool({ ...r.data.school, id: r.data.school._id });
      cacheSchool(r.data.school);
    } catch (e) {
      setError(e.response?.data?.error || 'Payment could not be recorded');
    } finally { setPaying(false); }
  };

  const logoutHome = () => {
    ['token', 'user', 'school'].forEach(k => localStorage.removeItem(k));
    navigate('/');
  };

  const status = school?.provisionStatus || 'PendingPayment';
  const activeStep = STATUS_STEP[status] ?? 0;

  // A submitted inquiry we can show live progress for (pending / approved)
  const inquiryProgress = noSchool && inquiry ? INQUIRY_STATUS[inquiry.status] : null;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f4ff', py: 4 }}>
      <Box sx={{ width: '100%', maxWidth: 560, px: 2 }}>
        <Box textAlign="center" mb={3}>
          <Apartment sx={{ fontSize: 44, color: '#1565c0' }} />
          <Typography variant="h5" fontWeight={800} color="#0a1931" mt={1}>
            {noSchool ? 'Almost there!' : (school?.name || 'Your School')}
          </Typography>
          {!noSchool && school?.code && <Chip label={school.code} size="small" sx={{ mt: 0.5 }} />}
        </Box>

        {!noSchool && (
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            <Step><StepLabel>Pay Commitment</StepLabel></Step>
            <Step><StepLabel>We Set Up Your Database</StepLabel></Step>
            <Step><StepLabel>Start Using EduMIS</StepLabel></Step>
          </Stepper>
        )}

        {inquiryProgress && (
          <Stepper activeStep={inquiryProgress.step} alternativeLabel sx={{ mb: 3 }}>
            <Step><StepLabel>Submitted</StepLabel></Step>
            <Step><StepLabel>Under Review</StepLabel></Step>
            <Step><StepLabel>Approved &amp; Set Up</StepLabel></Step>
          </Stepper>
        )}

        <Card elevation={4} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
              <Box textAlign="center" py={4}><CircularProgress /></Box>
            ) : inquiryProgress ? (
              // ── A submitted inquiry (pending / approved): show its live progress ──
              <Box textAlign="center" py={2}>
                <HourglassTop sx={{ fontSize: 56, color: '#1565c0', mb: 1 }} />
                <Typography variant="h6" fontWeight={700} mb={1}>{inquiryProgress.title}</Typography>
                {inquiry.schoolName && (
                  <Chip label={inquiry.schoolName} size="small" sx={{ mb: 1.5 }} />
                )}
                <Typography variant="body2" color="text.secondary" mb={2} lineHeight={1.8}>
                  {inquiryProgress.note}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" gap={1.5} justifyContent="center" flexWrap="wrap">
                  <Button variant="outlined" startIcon={<School />}
                    onClick={() => navigate('/school-inquiry')}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    View your inquiry
                  </Button>
                  <Button variant="text" startIcon={<Home />} onClick={logoutHome}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Back to Home
                  </Button>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="center" gap={1} color="text.secondary" mt={2.5}>
                  <CircularProgress size={14} />
                  <Typography variant="caption">Checking for updates… (auto-refreshes every 15s)</Typography>
                </Box>
              </Box>
            ) : noSchool ? (
              <Box textAlign="center" py={2}>
                <PersonSearch sx={{ fontSize: 56, color: '#1565c0', mb: 1 }} />
                <Typography variant="h6" fontWeight={700} mb={1}>Waiting for a school assignment</Typography>
                <Typography variant="body2" color="text.secondary" mb={2} lineHeight={1.8}>
                  Your account isn't linked to a school yet. If your school already uses EduMIS,
                  ask your school administrator to add you — this page will update automatically
                  once you've been assigned.
                </Typography>

                {inquiry && ['draft', 'rejected'].includes(inquiry.status) && (
                  <Box sx={{ bgcolor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 2, p: 2, mb: 2, textAlign: 'left' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="body2" fontWeight={700}>Your school inquiry</Typography>
                      <Chip size="small"
                        label={{ draft: 'Draft', rejected: 'Rejected' }[inquiry.status]}
                        color={{ draft: 'default', rejected: 'error' }[inquiry.status]} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {inquiry.schoolName || 'Your inquiry'} — {inquiry.status === 'rejected'
                        ? 'Not approved — you can edit and resubmit.'
                        : 'Draft not yet submitted.'}
                    </Typography>
                    <Box mt={1}>
                      <Button size="small" variant="outlined" onClick={() => navigate('/school-inquiry')}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>
                        View / edit your inquiry
                      </Button>
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" mb={1.5}>
                  {inquiry ? 'Prefer to register directly instead?' : 'Registering a new school?'}
                </Typography>
                <Box display="flex" gap={1.5} justifyContent="center" flexWrap="wrap">
                  {!inquiry && (
                    <Button variant="outlined" startIcon={<School />}
                      onClick={() => navigate('/school-inquiry')}
                      sx={{ textTransform: 'none', fontWeight: 700 }}>
                      Submit a School Inquiry
                    </Button>
                  )}
                  <Button variant="outlined" startIcon={<AddBusiness />}
                    onClick={() => navigate('/create-school')}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Create Your School
                  </Button>
                  <Button variant="text" startIcon={<Home />} onClick={logoutHome}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Back to Home
                  </Button>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="center" gap={1} color="text.secondary" mt={2.5}>
                  <CircularProgress size={14} />
                  <Typography variant="caption">Checking for assignment… (auto-refreshes every 15s)</Typography>
                </Box>
              </Box>
            ) : status === 'PendingPayment' ? (
              <>
                <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                  <Payment color="warning" />
                  <Typography variant="h6" fontWeight={700}>One step left — the commitment fee</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2.5} lineHeight={1.8}>
                  To activate your free trial we ask for a one-time activation fee of{' '}
                  <strong>KES {COMMITMENT_FEE.toLocaleString()}</strong>. It is{' '}
                  <strong>fully credited to your first invoice</strong> if you subscribe. Once paid, our
                  team sets up your school's own private database — ready within <strong>5 working hours</strong>.
                </Typography>
                <Box sx={{ bgcolor: '#e6510012', border: '1px solid #e6510040', borderRadius: 2, p: 2, mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Pay via M-Pesa / bank to Dataport Technologies, then click below. (In production this
                    confirms automatically from the payment gateway.)
                  </Typography>
                </Box>
                <Button variant="contained" fullWidth size="large" onClick={handlePay} disabled={paying}
                  startIcon={paying ? <CircularProgress size={18} color="inherit" /> : <Payment />}
                  sx={{ py: 1.4, fontWeight: 700, textTransform: 'none', bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}>
                  {paying ? 'Recording…' : `I've Paid the KES ${COMMITMENT_FEE.toLocaleString()} Fee`}
                </Button>
              </>
            ) : status === 'Provisioning' ? (
              <Box textAlign="center" py={2}>
                <HourglassTop sx={{ fontSize: 56, color: '#1565c0', mb: 1 }} />
                <Typography variant="h6" fontWeight={700} mb={1}>Setting up your school…</Typography>
                <Typography variant="body2" color="text.secondary" mb={2} lineHeight={1.8}>
                  Thank you! Your payment is recorded. Dataport is now creating your school's private
                  database and loading example data. This is ready within <strong>5 working hours</strong> —
                  you can close this page and we'll email you, or leave it open and it will refresh automatically.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" alignItems="center" justifyContent="center" gap={1} color="text.secondary">
                  <CircularProgress size={16} />
                  <Typography variant="caption">Checking status… (auto-refreshes every 15s)</Typography>
                </Box>
                <Button variant="text" sx={{ mt: 1 }} onClick={refresh}>Refresh now</Button>
              </Box>
            ) : (
              <Box textAlign="center" py={2}>
                <CheckCircle sx={{ fontSize: 56, color: '#2e7d32', mb: 1 }} />
                <Typography variant="h6" fontWeight={700} mb={1}>Your school is ready!</Typography>
                <Button variant="contained" onClick={() => navigate('/dashboard')} sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}>
                  Go to Dashboard
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        <Typography variant="caption" color="text.secondary" display="block" align="center" mt={2}>
          EduMIS by Dataport Technologies &nbsp;·&nbsp; EduMIS.dataportech.com
        </Typography>
      </Box>
    </Box>
  );
};

export default SchoolPending;
